/**
 * Admin V3 — Avoirs (credit notes) — list page
 *
 * Mounted at: /admin-v3/billing/avoirs
 * Source: GET /admin/billing/avoirs → { avoirs, count }  (ADMIN_PARTNER_LIST).
 *
 * Read-only ledger of credit notes. Creation happens from a Facture
 * (BillingInvoiceDetailPage → POST /admin/billing/avoirs); this page lists and
 * links into per-avoir detail. No mutations here.
 */

import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileMinus, ExternalLink, AlertCircle, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SectionCard, SmallPill, EmptyState, currency, avoirStatusMeta, cn } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';

const DEFAULT_LIMIT = 50;

const AvoirListPage = () => {
  const locale = DEFAULT_LOCALE;
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useAdminQuery('/admin/billing/avoirs', { limit: DEFAULT_LIMIT });
  const avoirs = Array.isArray(data?.avoirs) ? data.avoirs : [];
  const showSkeleton = loading && !data;

  const count = useMemo(() => (data?.count ?? avoirs.length), [data, avoirs]);

  return (
    <SectionCard title={t('billing.avoirs.title', locale)} icon={FileMinus} iconClass="bg-amber-100 text-amber-600">
      <div className="space-y-4">
        {data && (
          <div className="text-[12px] text-slate-500" data-testid="adminv3-avoirs-summary">
            {t('billing.avoirs.summary', locale).replace('{n}', String(count))}
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-1 flex items-start gap-3" data-testid="adminv3-avoirs-error">
            <AlertCircle className="h-5 w-5 text-rose-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-rose-700">{t('billing.avoirs.error.title', locale)}</p>
              <p className="text-xs text-rose-600 mt-1 break-words">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={refetch} className="border-rose-200 bg-white text-rose-700 hover:bg-rose-50">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t('billing.avoirs.error.retry', locale)}
            </Button>
          </div>
        )}

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {[
                  t('billing.avoirs.col.number', locale),
                  t('billing.avoirs.col.invoice', locale),
                  t('billing.avoirs.col.partner', locale),
                  t('billing.avoirs.col.reason', locale),
                  t('billing.avoirs.col.total', locale),
                  t('billing.avoirs.col.status', locale),
                  t('billing.avoirs.col.actions', locale),
                ].map((h, i) => (
                  <th key={i} className="px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {showSkeleton ? (
                Array(4).fill(0).map((_, i) => (
                  <tr key={`sk-${i}`} className="border-b border-slate-100">
                    {Array(7).fill(0).map((_, j) => (
                      <td key={j} className="px-4 py-3.5"><div className="h-3 w-full max-w-[120px] rounded bg-slate-100 animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : avoirs.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8">
                  <EmptyState icon={FileMinus} title={t('billing.avoirs.empty.title', locale)} desc={t('billing.avoirs.empty.desc', locale)} />
                </td></tr>
              ) : (
                avoirs.map((av) => {
                  const key = av.id || av.avoir_number;
                  const detailPath = `/admin/billing/avoirs/${key}`;
                  return (
                    <tr key={key} className="border-b border-slate-100 hover:bg-slate-50/70 transition-colors">
                      <td className="px-4 py-3.5">
                        <span className="inline-block rounded-lg bg-amber-50 px-2 py-1 font-mono text-[12px] font-bold text-amber-700">
                          {av.avoir_number || t('billing.avoirs.draftBadge', locale)}
                        </span>
                      </td>
                      <td className="px-4 py-3.5 font-mono text-[12px] text-slate-600">{av.original_invoice_number || '—'}</td>
                      <td className="px-4 py-3.5">
                        <div className="text-slate-900 font-medium text-[13px]">{av.partner_name || '—'}</div>
                      </td>
                      <td className="px-4 py-3.5 text-slate-600 text-[12px] max-w-[220px] truncate" title={av.reason || ''}>{av.reason || '—'}</td>
                      <td className={cn('px-4 py-3.5 font-bold whitespace-nowrap', (av.total_amount ?? 0) < 0 ? 'text-rose-700' : 'text-slate-900')}>
                        {currency(av.total_amount ?? 0)}
                      </td>
                      <td className="px-4 py-3.5"><SmallPill meta={avoirStatusMeta[av.status] || avoirStatusMeta.draft} /></td>
                      <td className="px-4 py-3.5">
                        <button type="button" onClick={() => navigate(detailPath)}
                                className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                                title={t('billing.avoirs.action.open', locale)} data-testid={`adminv3-avoirs-open-${key}`}>
                          <ExternalLink className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
};

export default AvoirListPage;
